/**
 * Email Verification Success Page
 * Shows confirmation after user clicks verification link in email
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import { PageTransition, MotionDiv } from '../lib/motion-utils';
import {
  AUTH_ROUTE_CONTEXT_PRESETS,
  enforcePublicRouteIsolation,
  resolveAuthRouteContextFromPreset,
} from '../lib/auth-route-isolation';

export default function EmailVerified() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Check for verification token in URL
    const token = searchParams.get('token') || searchParams.get('token_hash');
    const type = searchParams.get('type');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const verificationContext = resolveAuthRouteContextFromPreset(
      searchParams,
      AUTH_ROUTE_CONTEXT_PRESETS.emailVerification,
    );

    const runVerificationState = async () => {
      await enforcePublicRouteIsolation(verificationContext.hasContext);

      if (error) {
        setStatus('error');
        setErrorMessage(errorDescription || 'Verification failed');
        return;
      }

      if (type === 'signup' || token) {
        // Email verified successfully
        setStatus('success');
      } else {
        // Check if we're already logged in (Supabase redirects with session)
        const accessToken = localStorage.getItem('tension_access_token');
        if (accessToken) {
          setStatus('success');
        } else {
          setTimeout(() => {
            setStatus('success');
          }, 1500);
        }
      }
    };

    void runVerificationState();
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/auth');
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center p-4 tension-page-bg">
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="p-8 shadow-lg border border-neutral-100 bg-white">
            <div className="text-center space-y-6">
              {/* Status Icon */}
              <div className="flex justify-center">
                {status === 'verifying' && (
                  <div className="bg-purple-100 p-6 rounded-full">
                    <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
                  </div>
                )}
                {status === 'success' && (
                  <div className="bg-green-100 p-6 rounded-full">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                )}
                {status === 'error' && (
                  <div className="bg-red-100 p-6 rounded-full">
                    <XCircle className="w-12 h-12 text-red-600" />
                  </div>
                )}
              </div>

              {/* Title & Message */}
              {status === 'verifying' && (
                <>
                  <h1 className="text-2xl font-bold text-neutral-900">
                    Verifying Your Email
                  </h1>
                  <p className="text-neutral-600">
                    Please wait while we verify your email address...
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <h1 className="text-2xl font-bold text-neutral-900">
                    Email Verified! 🎉
                  </h1>
                  <p className="text-neutral-600">
                    Your email has been successfully verified. You can now sign in to your TENSION account and start tracking your relationship journey.
                  </p>

                  <div className="tension-glow bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-3 text-left">
                      <Mail className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div className="text-sm text-purple-900">
                        <p className="font-semibold mb-1">What's Next?</p>
                        <ul className="list-disc ml-4 space-y-1 text-purple-700">
                          <li>Sign in with your credentials</li>
                          <li>Choose your mode (Single or Relationship)</li>
                          <li>Complete your profile setup</li>
                          <li>Start your daily check-ins</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    onClick={handleContinue}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0"
                  >
                    Continue to Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              )}

              {status === 'error' && (
                <>
                  <h1 className="text-2xl font-bold text-neutral-900">
                    Verification Failed
                  </h1>
                  <p className="text-neutral-600">
                    {errorMessage || 'We couldn\'t verify your email address. The link may have expired or is invalid.'}
                  </p>

                  <div className="tension-glow bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">
                      Please try signing up again or contact support if the problem persists.
                    </p>
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => navigate('/auth')}
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </>
              )}
            </div>
          </Card>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-sm text-neutral-600">
              Need help?{' '}
              <button
                onClick={() => navigate('/support')}
                className="text-purple-600 hover:underline"
              >
                Contact Support
              </button>
            </p>
          </div>
        </MotionDiv>
      </div>
    </PageTransition>
  );
}
